import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { VideoDialog } from "app/components/parts/video-dialog";
import {
  buildActivationCheckoutLocationState,
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import { useGlobalSlice } from "app/slice";
import {
  getSecurityPlanId,
  normalizeSecurityPlanTierFromText,
  resolveIncludedSecurityPlanIdFromSimData,
  resolveIncludedSecurityPlanTierFromSimData,
  type SecurityPlanTier,
} from "../utils/included-security-plan-utils";
import { preloadedSimServiceFrequencyKeyToMonths } from "../utils/preloaded-sim-plan-mappers";
import { resolvePreloadedSimUpsellStepSkips } from "../utils/preloaded-sim-upsell-step-skips";
import { Button } from "app/components/ui/button";
import { Skeleton } from "app/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "app/components/ui/collapsible";

const formatPriceCompact = (value: unknown): string => {
  const n =
    typeof value === "number"
      ? value
      : Number.parseFloat(String(value ?? "").replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n)) return "$0";
  return Number.isInteger(n) ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
};

const parsePriceNumber = (value: unknown): number => {
  const n =
    typeof value === "number"
      ? value
      : Number.parseFloat(String(value ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const toMonthCount = (value: unknown): number => {
  const n =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

export const SecurityPlanUpgrade = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const simData = getActivationFlowData()?.simData;
    const skips = resolvePreloadedSimUpsellStepSkips(simData);
    if (!skips.skipSecurityPlan) return;
    if (skips.skipChooseServicePlan) {
      navigate("/activation/preloaded-sim/device-info", { replace: true });
      return;
    }
    navigate("/activation/preloaded-sim/choose-service-plan", {
      replace: true,
    });
  }, [navigate]);

  const [videoOpen, setVideoOpen] = useState(false);
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  const ctaLockRef = useRef(false);
  const activationData = getActivationFlowData();
  const includedPlanFromSimData = (
    activationData?.simData as Record<string, unknown> | undefined
  )?.planIncluded as Record<string, unknown> | undefined;
  const planFrequencyFromSim = Number.parseInt(
    String(includedPlanFromSimData?.frequency ?? "1"),
    10,
  );
  const chosenBillingMonths = preloadedSimServiceFrequencyKeyToMonths(
    activationData?.preloadedSimServiceFrequency,
  );
  const frequencyRaw =
    chosenBillingMonths != null && [1, 3, 6, 12].includes(chosenBillingMonths)
      ? chosenBillingMonths
      : planFrequencyFromSim;
  const requestFrequency =
    Number.isFinite(frequencyRaw) && [1, 3, 6, 12].includes(frequencyRaw)
      ? frequencyRaw
      : 1;
  const includedTierFromSimData = resolveIncludedSecurityPlanTierFromSimData(
    activationData?.simData,
  );
  const includedPlanIdFromSimData = resolveIncludedSecurityPlanIdFromSimData(
    activationData?.simData,
  );
  const { useGetMobileSimInventorySecurityPlansQuery } = useGlobalSlice();
  const securityPlansQueryParams = useMemo(
    () => ({
      frequency: requestFrequency,
      ...(includedTierFromSimData === "completeFamily"
        ? { securityPlanName: "Aura Family Complete" }
        : {}),
    }),
    [includedTierFromSimData, requestFrequency],
  );
  const securityPlansQuery = useGetMobileSimInventorySecurityPlansQuery(
    securityPlansQueryParams,
  );

  const securityPlans = securityPlansQuery.data?.securityPlans ?? [];
  const completeIndividualPlan =
    securityPlans.find((plan: any) =>
      String(plan?.securityPlanName || plan?.planName || "")
        .toLowerCase()
        .includes("individual"),
    ) ?? securityPlans[0];
  const completeFamilyPlan = securityPlans.find((plan: any) =>
    String(plan?.securityPlanName || plan?.planName || "")
      .toLowerCase()
      .includes("family"),
  );
  const includedPlanById = securityPlans.find(
    (plan: any) =>
      includedPlanIdFromSimData &&
      getSecurityPlanId(plan) === includedPlanIdFromSimData,
  );
  const includedPlanTier: SecurityPlanTier =
    normalizeSecurityPlanTierFromText(
      includedPlanById?.securityPlanName ?? includedPlanById?.planName,
    ) ||
    includedTierFromSimData ||
    "completeIndividual";
  const includedCompletePlan =
    includedPlanTier === "completeFamily"
      ? completeFamilyPlan || completeIndividualPlan
      : completeIndividualPlan;
  const includeFamilyOnly =
    includedTierFromSimData === "completeFamily" &&
    (securityPlansQuery.isLoading ||
      securityPlansQuery.isFetching ||
      Boolean(getSecurityPlanId(completeFamilyPlan)));
  const renderedPlanTypes = useMemo<Array<"individual" | "family">>(
    () => (includeFamilyOnly ? ["family"] : ["individual", "family"]),
    [includeFamilyOnly],
  );

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const isNowAboveSm = newWidth >= 640;
      setWindowWidth(newWidth);
      if (isNowAboveSm) {
        setOpenStates((prev) => {
          const next = { ...prev };
          renderedPlanTypes.forEach((p) => {
            next[p] = true;
          });
          return next;
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [renderedPlanTypes]);

  useEffect(() => {
    if (!renderedPlanTypes.length) return;
    setOpenStates((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const w = typeof window !== "undefined" ? window.innerWidth : 1024;
      const shouldBeOpen = w >= 640;
      const next: Record<string, boolean> = {};
      renderedPlanTypes.forEach((p) => {
        const isFamilyCard = p === "family";
        next[p] = shouldBeOpen ? true : isFamilyCard;
      });
      return next;
    });
  }, [renderedPlanTypes]);

  /** Loading / error / empty mirror choose-service-plan.tsx. */
  const isRequestPending =
    securityPlansQuery.isLoading || securityPlansQuery.isFetching;
  const isError = securityPlansQuery.isError;
  const error = securityPlansQuery.error;
  const securityUpgradeStepData: { data: unknown[] } | undefined =
    securityPlansQuery.data ? { data: securityPlans } : undefined;
  const securityUpgradeItems = securityUpgradeStepData?.data ?? [];
  const showEmptyPlans =
    !isRequestPending &&
    !isError &&
    securityUpgradeStepData !== undefined &&
    securityUpgradeItems.length === 0;
  const canUsePlanActions =
    !isRequestPending && !isError && securityUpgradeStepData !== undefined;
  const isSkeleton = isRequestPending;
  const refetchSecurityUpgradeStep = useCallback(() => {
    securityPlansQuery.refetch();
  }, [securityPlansQuery]);

  useEffect(() => {
    if (!activationData?.zipCode) {
      navigate("/activation", { replace: true });
      return;
    }
    if (
      !activationData.preloadedSimPrimaryDataTier ||
      !activationData.preloadedSimServiceFrequency
    ) {
      navigate("/activation/preloaded-sim/choose-service-plan", {
        replace: true,
      });
    }
  }, [
    navigate,
    activationData?.zipCode,
    activationData?.preloadedSimPrimaryDataTier,
    activationData?.preloadedSimServiceFrequency,
  ]);

  useEffect(() => {
    if (isRequestPending || isError) return;
    const planId = getSecurityPlanId(includedCompletePlan);
    if (!planId) return;
    const current = getActivationFlowData();
    if (String(current?.securityPlanId || "").trim() !== "") {
      return;
    }
    saveActivationFlowData({
      optForSecurityPlan: true,
      securityPlanId: planId,
    });
  }, [includedCompletePlan, isError, isRequestPending]);

  const goCheckoutKeepIndividual = useCallback(() => {
    if (ctaLockRef.current) return;
    ctaLockRef.current = true;
    try {
      saveActivationFlowData({
        optForSecurityPlan: true,
        securityPlanId: getSecurityPlanId(includedCompletePlan),
      });
      navigate("/checkout", {
        state: buildActivationCheckoutLocationState(),
      });
    } finally {
      window.setTimeout(() => {
        ctaLockRef.current = false;
      }, 800);
    }
  }, [includedCompletePlan, navigate]);

  const goCheckoutFamilyUpsell = useCallback(() => {
    if (ctaLockRef.current) return;
    ctaLockRef.current = true;
    try {
      saveActivationFlowData({
        optForSecurityPlan: true,
        securityPlanId:
          getSecurityPlanId(completeFamilyPlan) ||
          getSecurityPlanId(includedCompletePlan) ||
          undefined,
      });
      navigate("/checkout", {
        state: buildActivationCheckoutLocationState(),
      });
    } finally {
      window.setTimeout(() => {
        ctaLockRef.current = false;
      }, 800);
    }
  }, [completeFamilyPlan, includedCompletePlan, navigate]);

  if (isError) {
    return (
      <div className="px-5 md:px-6 xl:px-7 2xl:pr-[1.875rem] py-4 sm:py-6 md:py-8 lg:py-10 h-[calc(100vh-13.75rem)]">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-5 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
            Failed to load security upgrade options
          </h3>
          <p className="max-w-sm text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 sm:mb-4 lg:mb-5 mx-auto">
            {(error as any)?.data?.message ||
              "We couldn't retrieve the plans at this moment. Please try again."}
          </p>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
            onClick={() => refetchSecurityUpgradeStep()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (showEmptyPlans) {
    return (
      <div className="px-5 md:px-6 xl:px-7 2xl:pr-[1.875rem] py-4 sm:py-6 md:py-8 lg:py-10 h-[calc(100vh-13.75rem)]">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h3 className="max-w-2xl font-mobile text-[#1D1D1B] text-xl sm:text-2xl lg:text-3xl font-bold leading-5 sm:leading-8 lg:leading-9 mb-2 sm:mb-2.5 lg:mb-3 mx-auto">
            No security upgrade options to show right now
          </h3>
          <p className="max-w-md text-[#1D1D1B]/80 text-sm sm:text-[0.925rem] lg:text-base font-normal leading-5 sm:leading-[1.375rem] lg:leading-6 mb-3.5 sm:mb-4 lg:mb-5 mx-auto">
            We didn&apos;t receive any plan options from our catalog. This may
            be temporary—try refreshing. If it keeps happening, contact support
            and we&apos;ll help you finish activation.
          </p>
          <Button
            type="button"
            variant="primary"
            className="h-10 sm:h-12 md:h-[3.25rem] lg:h-[3.375rem] px-5 sm:px-6 md:px-8 lg:px-10 w-fit rounded-full"
            onClick={() => refetchSecurityUpgradeStep()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="w-full">
        <div className="text-center mb-4 sm:mb-6 lg:mb-8 px-1">
          <h2 className="font-mobile text-[#1D1D1B] text-[clamp(1.5rem,calc(2vw+1rem),2.25rem)] font-normal leading-snug md:leading-normal lg:leading-[3.3125rem] mb-1 sm:mb-2">
            Online Security &amp; Identity Protection Upgrade
          </h2>
          <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base lg:text-lg font-normal leading-[1.125rem] sm:leading-5 md:leading-6 lg:leading-7">
            Protect your whole family with this one time offer.
          </p>
        </div>

        <div className="flex flex-col xl:flex-row xl:items-stretch gap-6 sm:gap-7 md:gap-8 lg:gap-10 xl:gap-12 2xl:gap-14 justify-center items-center">
          {/* Video + marketing column */}
          <div className="w-full xl:w-64 xl:max-w-64 flex flex-col shrink-0">
            {isSkeleton ? (
              <Skeleton className="w-full rounded-2xl md:rounded-[1.125rem] aspect-[2.34/1] max-w-[33.625rem] xl:max-h-[14.375rem] bg-[#E2E4E9] mx-auto" />
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="relative hover:bg-transparent w-full rounded-2xl md:rounded-[1.125rem] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-[#E8E8E8] aspect-[2.34/1] max-w-[33.625rem] xl:max-h-[14.375rem] cursor-pointer group appearance-none p-0 mx-auto"
                onClick={() => setVideoOpen(true)}
              >
                <img
                  src="/assets/security-plan-upgrade-image.webp"
                  alt="Security Plan Upgrade"
                  className="w-full h-full object-cover select-none"
                  loading="lazy"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 group-hover:bg-gradient-to-b group-hover:from-transparent group-hover:to-black/50 transition-all duration-300">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[4.2188rem] h-[3.0094rem] sm:w-[4.4531rem] sm:h-[3.1766rem] lg:w-[4.6875rem] lg:h-[3.34375rem] rounded-3xl transition-transform duration-300 group-hover:scale-110">
                    <img
                      src="/assets/youtube-play-button.webp"
                      alt="Play button"
                      className="w-full h-full object-cover select-none"
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                </div>
              </Button>
            )}
            {isSkeleton ? (
              <div className="w-full max-w-sm sm:max-w-md lg:max-w-lg mx-auto mt-5 sm:mt-6 flex flex-col gap-1 sm:gap-1.5 md:gap-2 lg:gap-2.5">
                <Skeleton className="w-full h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
                <Skeleton className="w-full h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
                <Skeleton className="hidden xl:block w-full h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
                <Skeleton className="hidden xl:block w-full h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
                <Skeleton className="hidden xl:block w-full h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
                <Skeleton className="w-[85%] h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
                <Skeleton className="w-[60%] h-[1.375rem] sm:h-6 md:h-7 lg:h-8 bg-[#E2E4E9] rounded-md" />
              </div>
            ) : (
              <p className="max-w-sm sm:max-w-md lg:max-w-lg mx-auto mt-5 sm:mt-6 text-[#1D1D1B] text-[1.05rem] sm:text-[1.1875rem] md:text-[1.375rem] lg:text-2xl font-normal leading-snug sm:leading-6 md:leading-7 lg:leading-8 px-2.5 sm:px-3 lg:px-3.5">
                <span className="font-bold">Protect what matters most.</span>{" "}
                Upgrade to Family Complete and give your loved ones the peace of
                mind they deserve with Aura&apos;s best-in-class protection.
              </p>
            )}
          </div>

          {/* Plan cards */}
          <ul className="flex max-[916px]:flex-wrap-reverse flex-wrap lg:flex-nowrap gap-[1.125rem] justify-center w-full lg:w-max">
            {renderedPlanTypes.map((plan) => {
              const currentIncludedTier =
                securityPlansQuery.isLoading || securityPlansQuery.isFetching
                  ? includedTierFromSimData
                  : includedPlanTier;
              const isIncludedPlan =
                (plan === "individual" &&
                  currentIncludedTier === "completeIndividual") ||
                (plan === "family" && currentIncludedTier === "completeFamily");
              const planData =
                plan === "individual"
                  ? completeIndividualPlan
                  : completeFamilyPlan;
              const months = toMonthCount(
                planData?.frequency ?? requestFrequency,
              );
              const monthlyRegularBaseline = plan === "individual" ? 30 : 50;
              const regularTotalPrice = monthlyRegularBaseline * months;
              const currentPrice = parsePriceNumber(planData?.price);
              const computedSavings = Math.max(
                0,
                regularTotalPrice - currentPrice,
              );

              const rawPlanFeatures =
                plan === "individual"
                  ? completeIndividualPlan?.features
                  : completeFamilyPlan?.features;
              const baseSecurityFeatures = Array.isArray(rawPlanFeatures)
                ? rawPlanFeatures.map((f: unknown) => String(f))
                : [];
              const appendFamilySatisfactionGuarantee = plan === "family";
              const displaySecurityFeatures = appendFamilySatisfactionGuarantee
                ? [...baseSecurityFeatures, "14-day satisfaction guarantee"]
                : baseSecurityFeatures;
              const featureSkeletonCount = plan === "family" ? 7 : 6;

              return (
                <li
                  key={plan}
                  className="bg-white w-full lg:w-[25.5625rem] max-w-[25.5625rem] rounded-lg sm:rounded-xl overflow-hidden shadow-sm flex flex-col border border-[#EBEBEB]"
                >
                  <div className="flex justify-center items-center bg-black h-11 sm:h-[2.825rem] md:h-[2.875rem] shrink-0">
                    <h3 className="max-w-[12.5rem] sm:max-w-52 text-[0.72rem] sm:text-xs font-semibold leading-4 text-center text-white">
                      {isSkeleton ? (
                        plan === "individual" ? (
                          <Skeleton className="w-[13.75rem] sm:w-56 h-4 bg-white/20 rounded-md" />
                        ) : (
                          <div className="flex flex-col items-center gap-0.5">
                            <Skeleton className="w-[13.75rem] sm:w-56 h-4 bg-white/20 rounded-md" />
                            <Skeleton className="w-[9.75rem] sm:w-40 h-4 bg-white/20 rounded-md" />
                          </div>
                        )
                      ) : plan === "individual" ? (
                        "Identity, Fraud & Child Protection"
                      ) : (
                        "Identity, Fraud & Child Protection For 10 Adults and Unlimited Kids"
                      )}
                    </h3>
                  </div>
                  <Collapsible
                    open={
                      isSkeleton
                        ? true
                        : windowWidth >= 640
                        ? true
                        : openStates[plan] ?? false
                    }
                    onOpenChange={(open) => {
                      if (windowWidth < 640 && !isSkeleton) {
                        setOpenStates((prev) => ({ ...prev, [plan]: open }));
                      }
                    }}
                    className="flex flex-col flex-1 min-h-0"
                  >
                    <div className="p-4 sm:p-5 md:p-6 lg:p-[1.625rem] flex flex-col flex-1 min-h-0">
                      <div className="flex justify-between items-start gap-2">
                        <h4 className="text-black font-mobile text-[1.375rem] sm:text-2xl md:text-[1.625rem] font-medium leading-7 sm:leading-9 md:leading-[2.625rem] mb-2 sm:mb-3 md:mb-[0.815rem] flex-1 min-w-0">
                          {isSkeleton ? (
                            <Skeleton className="w-48 sm:w-[13.5rem] h-7 sm:h-9 md:h-[2.625rem] bg-[#E2E4E9] rounded-md" />
                          ) : plan === "individual" ? (
                            <>
                              <span className="sm:hidden">
                                Individual Complete
                              </span>
                              <span className="hidden sm:inline">
                                Complete Individual
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="sm:hidden">Family Complete</span>
                              <span className="hidden sm:inline">
                                Complete Family
                              </span>
                            </>
                          )}
                        </h4>
                        {!isSkeleton ? (
                          <CollapsibleTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              className="mb-2 sm:hidden hover:bg-transparent size-6"
                              aria-label="Toggle plan details"
                            >
                              <img
                                src="/assets/chevron-down-thick.svg"
                                alt="Chevron"
                                className={`size-full transition-transform ${
                                  windowWidth >= 640 || openStates[plan]
                                    ? "rotate-180"
                                    : ""
                                }`}
                              />
                            </Button>
                          </CollapsibleTrigger>
                        ) : null}
                      </div>
                      {isSkeleton ? (
                        <div className="flex items-center gap-1.5 sm:gap-2 py-1.5 md:py-2">
                          <div className="flex gap-1.5 sm:gap-2 items-end">
                            <Skeleton className="w-36 sm:w-[9.5rem] md:w-40 h-9 sm:h-10 md:h-11 bg-[#E2E4E9] rounded-md" />
                            {isIncludedPlan ? (
                              <Skeleton className="w-[7.5rem] sm:w-32 h-4 sm:h-5 md:h-6 bg-[#E2E4E9] rounded-md" />
                            ) : (
                              <Skeleton className="w-20 sm:w-[5.5rem] h-4 sm:h-5 md:h-6 bg-[#E2E4E9] rounded-md" />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 sm:gap-3 py-1.5 md:py-2">
                          <p className="text-[#98A2B3] font-mobile text-4xl sm:text-[2.5rem] md:text-[2.75rem] font-normal leading-10 sm:leading-[2.75rem] md:leading-[3rem] tracking-[-0.06rem] line-through">
                            {formatPriceCompact(regularTotalPrice)}
                          </p>
                          <div className="flex gap-1">
                            <p className="text-[#101828] font-mobile text-4xl sm:text-[2.5rem] md:text-[2.75rem] font-bold leading-10 sm:leading-[2.75rem] md:leading-[3rem] tracking-[-0.06rem]">
                              {formatPriceCompact(currentPrice)}
                            </p>
                            <span className="inline-block text-[#667085] text-sm sm:text-[0.925rem] md:text-base font-medium leading-4 sm:leading-5 md:leading-6 mt-[1.125rem] whitespace-nowrap">
                              {isIncludedPlan
                                ? months === 1
                                  ? "/mo Included"
                                  : `/${months} mo Included`
                                : months === 1
                                ? "/ month"
                                : `/${months} months`}
                            </span>
                          </div>
                        </div>
                      )}
                      <CollapsibleContent className="flex flex-col flex-1 min-h-0">
                        <ul className="flex-1 space-y-2.5 sm:space-y-3 md:space-y-4 lg:space-y-5 my-4 sm:my-5 md:my-6 lg:my-7">
                          {isSkeleton
                            ? Array.from({ length: featureSkeletonCount }).map(
                                (_, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                  >
                                    <Skeleton className="shrink-0 w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5 bg-[#E2E4E9] rounded mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem]" />
                                    <Skeleton className="w-40 sm:w-48 md:w-56 h-5 sm:h-6 bg-[#E2E4E9] rounded-md" />
                                  </li>
                                ),
                              )
                            : displaySecurityFeatures.map((feature) => (
                                <li
                                  key={feature}
                                  className="flex items-start gap-[0.375rem] sm:gap-[0.45rem] md:gap-[0.56rem]"
                                >
                                  <span className="shrink-0 mt-[0.1rem] sm:mt-[0.2rem] md:mt-[0.1rem] text-[#1EC677]">
                                    <svg
                                      className="w-4 h-4 sm:w-[1.125rem] sm:h-[1.125rem] md:w-5 md:h-5"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M2 12H22M22 12L15 7M22 12L15 17"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </span>
                                  <p className="text-[#1D1D1B] text-sm sm:text-[0.925rem] md:text-base leading-5 sm:leading-6 md:leading-relaxed">
                                    {String(feature)}
                                  </p>
                                </li>
                              ))}
                        </ul>
                        {isSkeleton ? (
                          <Skeleton className="w-48 sm:w-52 h-4 sm:h-5 bg-[#E2E4E9] rounded-md mb-4 sm:mb-5 md:mb-6 lg:mb-7" />
                        ) : computedSavings > 0 ? (
                          <p className="text-black text-[0.825rem] sm:text-sm font-bold leading-4 sm:leading-5 md:leading-[1.4375rem] mb-4 sm:mb-5 md:mb-6 lg:mb-7">
                            Save {formatPriceCompact(computedSavings)} on the
                            regular price
                          </p>
                        ) : (
                          <div className="mb-4 sm:mb-5 md:mb-6 lg:mb-7" />
                        )}
                        {isSkeleton ? (
                          <Skeleton className="w-full h-11 sm:h-[2.875rem] md:h-12 lg:h-[3.125rem] bg-[#E2E4E9] rounded-full" />
                        ) : (
                          <Button
                            type="button"
                            variant={isIncludedPlan ? "outline" : "primary"}
                            className={
                              isIncludedPlan
                                ? "capitalize h-11 sm:h-[2.875rem] md:h-12 lg:h-[3.125rem] rounded-[2.4375rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[0.825rem] sm:text-sm lg:text-[0.925rem] font-semibold"
                                : "capitalize h-11 sm:h-[2.875rem] md:h-12 lg:h-[3.125rem] rounded-[2.4375rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[0.825rem] sm:text-sm lg:text-[0.925rem] font-semibold text-white"
                            }
                            onClick={
                              isIncludedPlan
                                ? goCheckoutKeepIndividual
                                : goCheckoutFamilyUpsell
                            }
                            disabled={
                              !canUsePlanActions ||
                              (plan === "individual" &&
                                !getSecurityPlanId(completeIndividualPlan)) ||
                              (plan === "family" &&
                                !getSecurityPlanId(completeFamilyPlan))
                            }
                          >
                            {/* hover:bg-[#FAFAFA] border-[#EBEBEB] text-[#999999] */}
                            {isIncludedPlan
                              ? "Keep Current Plan"
                              : "I Want To Protect My Family"}
                          </Button>
                        )}
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <VideoDialog
        isOpen={videoOpen}
        onOpenChange={setVideoOpen}
        videoId="4haiomiHJSo"
      />
    </React.Fragment>
  );
};
